// src/pages/budgeting/boq/ProjectBOQList.jsx - UPDATED WITH TOWER STATUS
import React, { useState, useEffect } from 'react';
import { Eye, Search, ChevronDown, ChevronRight, Package, ArrowLeft, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import FOSegmentBOQForm from './FOSegmentBOQForm';
import TowerSiteBOQSitacForm from './TowerSiteBOQSitacForm';
import TowerSiteBOQCmeForm from './TowerSiteBOQCmeForm';
import projectService from '../../../services/projectService';
import foSegmentService from '../../../services/foSegmentService';
import towerSiteBOQService from '../../../services/towerSiteBOQService';

const ProjectBOQList = () => {
  const [view, setView] = useState('list');
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  
  // Data state
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState({});
  
  // Filter & Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const data = await projectService.getAllProjects();
      setProjects(data);
      console.log('✅ Fetched projects:', data);
    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    setIsLoadingDetails(prev => ({ ...prev, [projectId]: true }));
    try {
      const data = await projectService.getProjectByIdWithDetails(projectId);
      setProjects(prev => prev.map(p => p.id === projectId ? data : p));
      console.log('✅ Fetched project details:', data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching project details:', error);
      toast.error('Failed to load project details');
      return null;
    } finally {
      setIsLoadingDetails(prev => ({ ...prev, [projectId]: false }));
    }
  };

  const toggleExpandRow = async (project) => {
    const projectId = project.id;
    
    if (expandedRows.includes(projectId)) {
      setExpandedRows(prev => prev.filter(id => id !== projectId));
      return;
    }
    
    setExpandedRows(prev => [...prev, projectId]);
    
    const needsFetch = project.projectType === 'TOWER' 
      ? !project.towerSites || project.towerSites.length === 0
      : !project.foSegments || project.foSegments.length === 0;
    
    if (needsFetch) {
      await fetchProjectDetails(projectId);
    }
  };

  const handleView = async (project) => {
    const detailedProject = await fetchProjectDetails(project.id);
    if (detailedProject) {
      setSelectedProject(detailedProject);
      setView('detail');
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedProject(null);
    fetchProjects();
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.projectCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.customerName && project.customerName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'ALL' || project.projectType === filterType;
    const matchesStatus = filterStatus === 'ALL' || project.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  if (view === 'detail' && selectedProject) {
    return <ProjectDetail project={selectedProject} onBack={handleBackToList} />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Project Bill of Quantity List</h1>
            <p className="text-gray-600 mt-1">Manage construction projects Bill of Quantity</p>
          </div>
          <button
            onClick={fetchProjects}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Search and Filter Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by project name, code, or customer..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-w-[150px]"
            >
              <option value="ALL">All Types</option>
              <option value="TOWER">TOWER</option>
              <option value="FO">FO</option>
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-w-[150px]"
            >
              <option value="ALL">All Status</option>
              <option value="STARTED">Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="COMPLETED">Completed</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">No</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Project Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Project Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="flex space-x-2">
                        <div className="w-3 h-3 bg-red-400 rounded-full animate-bounce"></div>
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <p className="text-gray-500 text-sm">Loading projects...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <Package className="w-16 h-16 text-gray-300" />
                      <p className="text-gray-500 text-sm font-medium">No projects found</p>
                      <p className="text-gray-400 text-xs">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProjects.map((project, index) => (
                  <React.Fragment key={project.id}>
                    <tr className="hover:bg-red-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">{project.projectCode}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="font-semibold text-gray-900">{project.projectName}</div>
                        <div className="text-gray-500 text-xs mt-1">
                          Progress: <span className="font-medium text-blue-600">{project.totalProgress?.toFixed(1) || 0}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{project.customerName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          project.projectType === 'TOWER' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {project.projectType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          project.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          project.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                          project.status === 'ON_HOLD' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => toggleExpandRow(project)}
                          disabled={isLoadingDetails[project.id]}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isLoadingDetails[project.id] ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : expandedRows.includes(project.id) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span className="font-semibold">
                            {project.projectType === 'TOWER' 
                              ? `${project.towerSiteCount || 0} Sites` 
                              : `${project.foSegmentCount || 0} Segments`}
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button 
                          onClick={() => handleView(project)} 
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                    
                    {expandedRows.includes(project.id) && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 bg-gradient-to-r from-blue-50 to-gray-50">
                          <div className="ml-8">
                            <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                              <Package className="w-4 h-4 text-red-600" />
                              {project.projectType === 'TOWER' ? 'Tower Sites:' : 'FO Segments:'}
                            </h4>
                            
                            {isLoadingDetails[project.id] ? (
                              <div className="text-center py-4">
                                <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
                                <p className="text-sm text-gray-500 mt-2">Loading details...</p>
                              </div>
                            ) : project.projectType === 'TOWER' ? (
                              project.towerSites && project.towerSites.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  {project.towerSites.map(site => (
                                    <div key={site.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                                      <div className="flex items-center gap-3 mb-2">
                                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                          <Package className="w-4 h-4 text-purple-600" />
                                        </div>
                                        <div className="flex-1">
                                          <span className="font-semibold text-sm text-gray-900 block">{site.siteName}</span>
                                          <span className="text-xs text-gray-500">{site.siteId}</span>
                                        </div>
                                      </div>
                                      <div className="text-xs text-gray-600 ml-11 space-y-1">
                                        <div>{site.regional}</div>
                                        <div className="flex items-center justify-between">
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                            {site.typeSiteContract}
                                          </span>
                                          <span className="text-gray-500">{site.towerHeight}m</span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-4">No tower sites data available</p>
                              )
                            ) : (
                              project.foSegments && project.foSegments.length > 0 ? (
                                <div className="space-y-2">
                                  {project.foSegments.map(segment => (
                                    <div key={segment.id} className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                          <Package className="w-4 h-4 text-green-600" />
                                        </div>
                                        <div>
                                          <div className="font-semibold text-sm text-gray-900">{segment.siteNameAB}</div>
                                          <div className="text-xs text-gray-600 mt-1 space-x-2">
                                            <span>{segment.spanId}</span>
                                            <span>•</span>
                                            <span>{segment.mainArea}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                          {segment.ringType}
                                        </span>
                                        <span className="text-sm font-medium text-gray-700">
                                          {(segment.commercialDistance / 1000).toFixed(2)} km
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-4">No FO segments data available</p>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium text-red-600">{filteredProjects.length}</span> of <span className="font-medium">{projects.length}</span> projects
            </div>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                  TOWER
                </span>
                <span className="text-gray-700 font-medium">{projects.filter(p => p.projectType === 'TOWER').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                  FO
                </span>
                <span className="text-gray-700 font-medium">{projects.filter(p => p.projectType === 'FO').length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= PROJECT DETAIL COMPONENT WITH TOWER STATUS =============
const ProjectDetail = ({ project, onBack }) => {
  const [view, setView] = useState('detail');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedBOQType, setSelectedBOQType] = useState(null); // 'SITAC' or 'CME'
  
  // Track BOQ status for FO segments
  const [segmentBOQStatus, setSegmentBOQStatus] = useState({});
  const [isCheckingBOQ, setIsCheckingBOQ] = useState(false);
  
  // ✅ NEW: Track BOQ status for Tower sites (SITAC & CME)
  const [towerSiteBOQStatus, setTowerSiteBOQStatus] = useState({});
  const [isCheckingTowerBOQ, setIsCheckingTowerBOQ] = useState(false);

  // Check BOQ status on mount
  useEffect(() => {
    if (project.projectType === 'FO') {
      checkFOSegmentBOQStatus();
    } else if (project.projectType === 'TOWER') {
      checkTowerSiteBOQStatus();
    }
  }, [project]);
  
  // ✅ Check FO Segment BOQ Status (existing)
  const checkFOSegmentBOQStatus = async () => {
    if (!project.foSegments || project.foSegments.length === 0) return;
    
    setIsCheckingBOQ(true);
    try {
      const foSegmentBOQService = (await import('../../../services/foSegmentBOQService')).default;
      
      console.log('🔍 Checking BOQ status for', project.foSegments.length, 'segments...');
      
      const statusPromises = project.foSegments.map(async (segment) => {
        try {
          const exists = await foSegmentBOQService.checkBOQExists(segment.id);
          console.log(`✅ Segment ${segment.id} (${segment.siteNameAB}): BOQ exists = ${exists}`);
          return { id: segment.id, hasBOQ: exists };
        } catch (error) {
          console.error(`❌ Error checking BOQ for segment ${segment.id}:`, error);
          return { id: segment.id, hasBOQ: false };
        }
      });
      
      const results = await Promise.all(statusPromises);
      const statusMap = {};
      results.forEach(({ id, hasBOQ }) => {
        statusMap[id] = hasBOQ;
      });
      
      setSegmentBOQStatus(statusMap);
      console.log('✅ FO BOQ status loaded:', statusMap);
    } catch (error) {
      console.error('❌ Error checking FO BOQ status:', error);
    } finally {
      setIsCheckingBOQ(false);
    }
  };
  
  // ✅ NEW: Check Tower Site BOQ Status (SITAC & CME)
  const checkTowerSiteBOQStatus = async () => {
    if (!project.towerSites || project.towerSites.length === 0) return;
    
    setIsCheckingTowerBOQ(true);
    try {
      console.log('🔍 Checking BOQ status for', project.towerSites.length, 'tower sites...');
      
      const statusPromises = project.towerSites.map(async (site) => {
        try {
          const status = await towerSiteBOQService.getBOQStatus(site.id);
          console.log(`✅ Tower Site ${site.id} (${site.siteName}): SITAC=${status.hasSitacBOQ}, CME=${status.hasCmeBOQ}`);
          return { 
            id: site.id, 
            hasSitacBOQ: status.hasSitacBOQ,
            hasCmeBOQ: status.hasCmeBOQ
          };
        } catch (error) {
          console.error(`❌ Error checking BOQ for tower site ${site.id}:`, error);
          return { id: site.id, hasSitacBOQ: false, hasCmeBOQ: false };
        }
      });
      
      const results = await Promise.all(statusPromises);
      const statusMap = {};
      results.forEach(({ id, hasSitacBOQ, hasCmeBOQ }) => {
        statusMap[id] = { hasSitacBOQ, hasCmeBOQ };
      });
      
      setTowerSiteBOQStatus(statusMap);
      console.log('✅ Tower BOQ status loaded:', statusMap);
    } catch (error) {
      console.error('❌ Error checking Tower BOQ status:', error);
    } finally {
      setIsCheckingTowerBOQ(false);
    }
  };

  const handleCreateBOQ = async (item, boqType = null) => {
    console.log('🔄 Loading data for BOQ...', {
      id: item.id,
      name: item.siteNameAB || item.siteName,
      boqType
    });
    
    try {
      if (project.projectType === 'FO') {
        const hasBOQ = segmentBOQStatus[item.id] ?? item.hasBOQ ?? false;
        const foSegmentBOQService = (await import('../../../services/foSegmentBOQService')).default;
        
        // Fetch fresh segment data
        const freshSegment = await foSegmentService.getFOSegmentById(item.id);
        
        console.log('✅ Fresh segment loaded:', {
          id: freshSegment.id,
          mainArea: freshSegment.mainArea,
          priceCoefficient: freshSegment.priceCoefficient,
          hasBOQ: hasBOQ
        });
        
        // If BOQ exists, load it
        if (hasBOQ) {
          try {
            console.log('📦 Loading existing BOQ for segment:', freshSegment.id);
            const existingBOQ = await foSegmentBOQService.getBOQBySegmentId(freshSegment.id);
            
            if (existingBOQ) {
              console.log('✅ BOQ loaded successfully:', {
                id: existingBOQ.id,
                itemCount: existingBOQ.itemCount,
                subtotal: existingBOQ.subtotalAmount
              });
              
              setSelectedItem({
                ...freshSegment,
                existingBOQ: existingBOQ
              });
            } else {
              console.log('⚠️ BOQ exists flag is true but data not found');
              setSelectedItem(freshSegment);
            }
          } catch (boqError) {
            console.error('❌ Error loading BOQ:', boqError);
            toast.error('Failed to load existing BOQ');
            setSelectedItem(freshSegment);
          }
        } else {
          console.log('📝 Creating new BOQ');
          setSelectedItem(freshSegment);
        }
        
        setView('segment-boq');
        
      } else {
        // TOWER project
        setSelectedItem(item);
        setSelectedBOQType(boqType);
        setView(boqType === 'SITAC' ? 'tower-boq-sitac' : 'tower-boq-cme');
      }
      
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error('Failed to load item details');
      setSelectedItem(item);
      setSelectedBOQType(boqType);
      setView(project.projectType === 'TOWER' 
        ? (boqType === 'SITAC' ? 'tower-boq-sitac' : 'tower-boq-cme')
        : 'segment-boq'
      );
    }
  };

  const handleBackToDetail = () => {
    setView('detail');
    setSelectedItem(null);
    setSelectedBOQType(null);
    onBack();
  };

  const handleBOQSaved = async (itemId) => {
    console.log('✅ BOQ saved for item:', itemId);
    toast.success('BOQ saved successfully!');
    
    // Update status
    if (project.projectType === 'FO') {
      setSegmentBOQStatus(prev => ({
        ...prev,
        [itemId]: true
      }));
    } else {
      // Refresh tower BOQ status
      await checkTowerSiteBOQStatus();
    }
    
    handleBackToDetail();
  };

  // Render FO Segment BOQ Form
  if (view === 'segment-boq' && selectedItem) {
    return (
      <FOSegmentBOQForm
        segment={selectedItem}
        onBack={handleBackToDetail}
        onSave={handleBOQSaved}
      />
    );
  }

  // ✅ Render Tower SITAC BOQ Form
  if (view === 'tower-boq-sitac' && selectedItem) {
    return (
      <TowerSiteBOQSitacForm
        site={selectedItem}
        onBack={handleBackToDetail}
        onSave={handleBOQSaved}
      />
    );
  }

  // ✅ Render Tower CME BOQ Form
  if (view === 'tower-boq-cme' && selectedItem) {
    return (
      <TowerSiteBOQCmeForm
        site={selectedItem}
        onBack={handleBackToDetail}
        onSave={handleBOQSaved}
      />
    );
  }

  // Render Project Detail
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Project Details</h1>
              <p className="text-gray-600 mt-1">{project.projectCode}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Info Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <h3 className="text-lg font-bold text-gray-800">Project Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Project Code</label>
              <p className="text-lg font-semibold text-gray-900">{project.projectCode}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Project Type</label>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                project.projectType === 'TOWER' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
              }`}>
                {project.projectType}
              </span>
            </div>
            <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Project Name</label>
              <p className="text-lg font-medium text-gray-900">{project.projectName}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Customer</label>
              <p className="text-lg font-medium text-gray-900">{project.customerName || '-'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Status</label>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                project.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                project.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                project.status === 'ON_HOLD' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {project.status}
              </span>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Progress</label>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${project.totalProgress || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold text-blue-600">
                  {project.totalProgress?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Total Budget</label>
              <p className="text-lg font-semibold text-green-600">
                {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(project.totalBudget || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">
              {project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments'} 
              <span className="ml-2 text-sm font-normal text-gray-600">
                ({project.projectType === 'TOWER' 
                  ? (project.towerSites?.length || 0)
                  : (project.foSegments?.length || 0)} items)
              </span>
            </h3>
            {(isCheckingBOQ || isCheckingTowerBOQ) && (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Checking BOQ status...</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Table for Tower Sites with SITAC & CME Status */}
          {project.projectType === 'TOWER' && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">No</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Site Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Regional</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Height</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {project.towerSites && project.towerSites.length > 0 ? (
                    project.towerSites.map((site, idx) => {
                      const status = towerSiteBOQStatus[site.id] || { hasSitacBOQ: false, hasCmeBOQ: false };
                      
                      return (
                        <tr key={site.id} className="hover:bg-red-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{idx + 1}</td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Package className="w-4 h-4 text-purple-600" />
                              </div>
                              <div>
                                <span className="font-semibold text-gray-900">{site.siteName}</span>
                                <div className="text-xs text-gray-500">{site.siteId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{site.regional}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                              {site.typeSiteContract}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{site.towerHeight}m</td>
                          
                          {/* ✅ STATUS COLUMN - Shows SITAC & CME Status */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              {/* SITAC Status */}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                status.hasSitacBOQ 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                SITAC {status.hasSitacBOQ ? '✓' : ''}
                              </span>
                              {/* CME Status */}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                status.hasCmeBOQ 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                CME {status.hasCmeBOQ ? '✓' : ''}
                              </span>
                            </div>
                          </td>
                          
                          {/* ACTION COLUMN - SITAC & CME Buttons */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex flex-col gap-2">
                              {/* SITAC BOQ Button */}
                              <button
                                onClick={() => handleCreateBOQ(site, 'SITAC')}
                                disabled={isCheckingTowerBOQ}
                                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all shadow-sm hover:shadow disabled:opacity-50 ${
                                  status.hasSitacBOQ
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-purple-600 text-white hover:bg-purple-700'
                                }`}
                              >
                                {status.hasSitacBOQ ? 'Edit BOQ SITAC' : 'Create BOQ SITAC'}
                              </button>
                              
                              {/* CME BOQ Button - Disabled if SITAC doesn't exist */}
                              <button
                                onClick={() => handleCreateBOQ(site, 'CME')}
                                disabled={isCheckingTowerBOQ || !status.hasSitacBOQ}
                                className={`px-3 py-1.5 rounded text-xs font-semibold transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed ${
                                  status.hasCmeBOQ
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : status.hasSitacBOQ
                                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                                title={!status.hasSitacBOQ ? 'Create SITAC BOQ first' : ''}
                              >
                                {status.hasCmeBOQ ? 'Edit BOQ CME' : 'Create BOQ CME'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No tower sites available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Table for FO Segments (existing) */}
          {project.projectType === 'FO' && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">No</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Segment</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Route</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Main Area</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Length</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {project.foSegments && project.foSegments.length > 0 ? (
                    project.foSegments.map((segment, idx) => {
                      const hasBOQ = segmentBOQStatus[segment.id] ?? segment.hasBOQ ?? false;
                      
                      return (
                        <tr key={segment.id} className="hover:bg-red-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{idx + 1}</td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <Package className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <span className="font-semibold text-gray-900">{segment.siteNameAB}</span>
                                <div className="text-xs text-gray-500">{segment.spanId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              {segment.ringType}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{segment.mainArea}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="font-semibold text-gray-900">
                              {(segment.commercialDistance / 1000).toFixed(2)} km
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleCreateBOQ(segment)}
                              disabled={isCheckingBOQ}
                              className={`font-medium px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 ${
                                hasBOQ
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              {hasBOQ ? 'Edit BOQ' : 'Create BOQ'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No FO segments available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectBOQList;